import './CollapsibleBottomBar.css';

import {
  type FC,
  type ReactElement,
  type ReactNode,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';

import type * as React from 'react';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useStableValue } from '@ipfabric/ui-components/hooks/useStableValue';
import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import { sleep } from '@ipfabric/ui-components/utils/sleep';
import classNames from 'classnames';
import { pipe as fpPipe } from 'fp-ts/lib/function';
import { assoc, pipe } from 'ramda';
import { Transition } from 'react-transition-group';
import type { TransitionStatus } from 'react-transition-group/Transition';
import { animations } from 'utils/constants';

const MIN_PANEL_HEIGHT = 200;

type BottomBarHeaderConfig = {
  isDragging: boolean;
  onClose: () => void;
};

interface Props {
  container: HTMLElement | null;
  content: () => ReactElement;
  contentClassName?: string;
  defaultHeight: number;
  dragElementClassName: string;
  header: (methods: BottomBarHeaderConfig) => ReactElement;
  hideOnClose: boolean;
  onClick?: () => void;
  onClose: (data?: any) => void;
  open: boolean;
  /* Unique identifier for localStorage. */
  /* localStorage settings can be different for DeviceView and for other component. */
  storagePrefixName: string;
  wrapperClassName?: string;
}

const getBarClassName = (transitionState: TransitionStatus): string => {
  switch (transitionState) {
    case 'entering':
    case 'entered':
      return 'collapsible-bottom-bar--open';
    case 'exiting':
    case 'exited':
      return '';
    default:
      return '';
  }
};

const getPanelCollapsing = (transitionState: TransitionStatus): boolean => {
  switch (transitionState) {
    case 'entering':
    case 'entered':
      return false;
    default:
      return true;
  }
};

const getInitialHeight = (
  localStorageKey: string,
  defaultHeight: number,
  container: HTMLElement | null,
): number => {
  const storageHeight = localStorage.getItem(localStorageKey);
  return fpPipe(
    storageHeight
      ? Number(storageHeight)
      : Math.max(defaultHeight, MIN_PANEL_HEIGHT),
    (height) => {
      if (container === null) {
        return height;
      }
      const { offsetHeight } = container;
      return height > offsetHeight ? offsetHeight : height;
    },
  );
};

const BareCollapsibleBottomBar: FC<Props> = (props) => {
  const localStorageKey = `${props.storagePrefixName}-height`;

  const [barOpen, setBarOpen] = useState<boolean>(false);
  // Bar height will be computed as soon as bar element is rendered and bar's height can be obtained
  const height = useRef<number>(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const lastY = useRef<number>(0);

  const handleClose = useStableCallback(async (data?: any) => {
    if (props.hideOnClose || !data) {
      setBarOpen(false);
      await sleep(animations.FASTEST);
    }
    props.onClose(data);
  });

  const handleDrag = useStableCallback((e: MouseEvent) => {
    if (panelRef.current && isDragging) {
      const currentHeight = panelRef.current.clientHeight;
      const diffY = lastY.current - e.clientY;
      const total = currentHeight + diffY;
      const computedHeight = pipe(
        (computed: number) =>
          computed < MIN_PANEL_HEIGHT ? MIN_PANEL_HEIGHT : computed,
        (computed: number) => {
          if (!props.container) {
            return computed;
          }
          const containerDimensions = props.container.getBoundingClientRect();
          if (computed > containerDimensions.height) {
            return containerDimensions.height;
          }
          return computed;
        },
      )(total);
      panelRef.current.style.height = `${computedHeight}px`;
      height.current = computedHeight;
      lastY.current = e.clientY;
    }
  });

  const dragEventListeners = useStableValue(
    () => new ManagedDomEventListeners(),
  );

  const handleDragEnd = useStableCallback(() => {
    setIsDragging(false);
    localStorage.setItem(localStorageKey, height.current.toString());
    dragEventListeners.remove();
  });

  const handleDragStart = useStableCallback((e: React.MouseEvent) => {
    lastY.current = e.clientY;
    dragEventListeners.add(
      [window, 'mousemove', handleDrag],
      [window, 'mouseup', handleDragEnd],
    );
    setIsDragging(true);
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount
  useEffect(() => {
    height.current = getInitialHeight(
      localStorageKey,
      props.defaultHeight,
      props.container,
    );
    if (props.open) {
      setBarOpen(true);
    }
  }, []);

  useEffect(() => {
    return () => {
      dragEventListeners.remove();
    };
  }, []);

  const renderContent = (transitionState: TransitionStatus): ReactNode => {
    const panelStyle = { height: `${height.current}px` };
    return (
      <div
        className={`collapsible-bottom-bar flex flex-col ${getBarClassName(transitionState)} ${props.wrapperClassName}`}
        ref={panelRef}
        onClick={() => {
          props.onClick?.();
        }}
        style={
          getPanelCollapsing(transitionState)
            ? assoc('marginBottom', `-${height.current}px`, panelStyle)
            : panelStyle
        }
      >
        <div
          className={classNames(
            props.dragElementClassName,
            'pointer-events-auto',
          )}
          onMouseDown={handleDragStart}
        />
        <div className='collapsible-bottom-bar__wrapper my-0 flex flex-1 flex-col overflow-y-hidden'>
          <div className='collapsible-bottom-bar__header'>
            {props.header({ onClose: handleClose, isDragging })}
          </div>
          <div
            className={classNames(
              props.contentClassName,
              'collapsible-bottom-bar__content flex-1 overflow-hidden',
              isDragging ? 'pointer-events-none' : 'pointer-events-auto',
            )}
          >
            {props.content()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Transition in={barOpen} timeout={animations.FASTEST} nodeRef={panelRef}>
      {renderContent}
    </Transition>
  );
};

export const CollapsibleBottomBar = memo(BareCollapsibleBottomBar);
